import React, { useMemo } from 'react'
import moment from 'moment-business-days'
import styles from './styles.module.scss'
import { Absence } from 'Types/Absences/Absence'
import { Filter } from 'Types/Filter'
import { IHoliday } from 'Types/Holidays/IHoliday'
import {
  DATE_FORMAT,
  expandAbsenceDates,
  getMonthRange,
  toHolidayDates,
} from '../utils/absenceDays'

const HOURS_PER_DAY = 8

interface IProps {
  workingHours: number | undefined
  holidays: IHoliday[] | undefined
  filters: Filter[]
  absences: Absence[]
}

const MonthOverview = ({ workingHours, holidays, filters, absences }: IProps) => {
  const { monthStart, monthEnd } = useMemo(() => getMonthRange(filters), [filters])

  const { workingDays, daysOff } = useMemo(() => {
    const holidayDates = toHolidayDates(holidays)
    const absenceDates = expandAbsenceDates(absences, monthStart, monthEnd)
    const businessDays = moment(monthStart)
      .monthBusinessDays()
      .map((date) => date.format(DATE_FORMAT))
      .filter((date) => !holidayDates.has(date))

    return {
      workingDays: businessDays.length,
      daysOff: businessDays.filter((date) => absenceDates.has(date)).length,
    }
  }, [holidays, absences, monthStart, monthEnd])

  // The holiday API returns some days twice under different names, show each date once
  const monthHolidays = useMemo(() => {
    const byDate = new Map<string, IHoliday>()
    ;(holidays ?? []).forEach((holiday) => {
      const key = moment(holiday.date).format(DATE_FORMAT)
      if (!byDate.has(key)) byDate.set(key, holiday)
    })
    return Array.from(byDate.values()).sort((a, b) => moment(a.date).diff(moment(b.date)))
  }, [holidays])

  const loggedHours = workingHours ?? 0
  // Time off days don't need to be logged, so they don't count towards the target
  const expectedHours = (workingDays - daysOff) * HOURS_PER_DAY
  const percentage = expectedHours ? Math.round((loggedHours / expectedHours) * 100) : 0

  return (
    <div className={styles.container}>
      <div className={styles.month}>{moment(monthStart).format('MMMM YYYY')}</div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.label}>Hours logged</span>
          <div className={styles.valueRow}>
            <span className={styles.value}>
              {loggedHours}h <span className={styles.muted}>/ {expectedHours}h</span>
            </span>
            <span className={styles.badge}>{percentage}%</span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressBar}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>

        <div className={styles.stat}>
          <span className={styles.label}>Working days</span>
          <span className={styles.value}>{workingDays}</span>
          {daysOff > 0 && (
            <span className={styles.muted}>
              {workingDays - daysOff} after {daysOff} {daysOff === 1 ? 'day' : 'days'} off
            </span>
          )}
        </div>

        <div className={styles.stat}>
          <span className={styles.label}>Holidays</span>
          {monthHolidays.length > 0 ? (
            <ul className={styles.holidays}>
              {monthHolidays.map((holiday) => (
                <li key={moment(holiday.date).format(DATE_FORMAT)}>
                  <span className={styles.holidayDate}>
                    {moment(holiday.date).format('ddd DD.MM.')}
                  </span>
                  {holiday.name}
                </li>
              ))}
            </ul>
          ) : (
            <span className={styles.muted}>No holidays this month</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default MonthOverview
